import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";

const API = `/api`;

const field =
  "w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-sm text-[#FAF8F5] placeholder:text-[#626773] transition-colors duration-300";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Lien invalide ou expiré.");
      } else {
        setDone(true);
      }
    } catch {
      setError("Connexion impossible au serveur");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-reset-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-ink-card border border-white/5 p-10"
      >
        <div className="flex items-center gap-3 mb-10">
          <img src="/logo-mark.png" alt="e².law" className="w-9 h-9" />
          <div>
            <p className="font-serif text-xl">Nouveau mot de passe</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#626773]">
              Back-office e².law
            </p>
          </div>
        </div>

        {!token ? (
          <p className="text-sm text-red-400" data-testid="admin-reset-invalid">
            Lien invalide — refaites une demande depuis la page de connexion.
          </p>
        ) : done ? (
          <>
            <p className="text-sm text-gold leading-relaxed" data-testid="admin-reset-done">
              Votre mot de passe a été modifié. Vous pouvez vous connecter.
            </p>
            <Link
              to="/admin/login"
              data-testid="admin-reset-login-link"
              className="mt-8 inline-block bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-8 py-4 hover:bg-gold-light transition-colors"
            >
              Se connecter
            </Link>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="space-y-7">
              <div>
                <label htmlFor="reset-password" className="label-accent block mb-3">Nouveau mot de passe</label>
                <input
                  id="reset-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="8 caractères minimum"
                  className={field}
                  data-testid="admin-reset-password-input"
                />
              </div>
              <div>
                <label htmlFor="reset-confirm" className="label-accent block mb-3">Confirmer</label>
                <input
                  id="reset-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••••••"
                  className={field}
                  data-testid="admin-reset-confirm-input"
                />
              </div>
            </div>

            {error && (
              <p className="mt-6 text-xs text-red-400" data-testid="admin-reset-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-reset-submit"
              className="mt-10 w-full bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium py-4 hover:bg-gold-light transition-colors disabled:opacity-50"
            >
              {loading ? "Enregistrement…" : "Enregistrer le mot de passe"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
